import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, FlaskConical, Truck, ShieldCheck } from "lucide-react";
import { MaskedLines, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { ProductCard } from "../components/ProductCard";
import { StackCard } from "../components/StackCard";
import { fetchProducts, fetchGoals, fetchStacks } from "../lib/api";

const GOAL_IMG = {
  recovery:
    "https://images.pexels.com/photos/29526364/pexels-photo-29526364.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=900",
  lean: "https://images.pexels.com/photos/850293/pexels-photo-850293.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=900",
  performance:
    "https://images.pexels.com/photos/12698200/pexels-photo-12698200.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=900",
  essentials:
    "https://images.unsplash.com/photo-1781121447997-fda9d37597cb?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
};

const CHAPTERS = [
  {
    n: "01",
    title: "Purity is non-negotiable",
    body: "Every batch is third-party HPLC tested to greater than 99% purity. No guesswork, no backyard chemistry — certificates of analysis available on request.",
    img: "https://images.pexels.com/photos/5922101/pexels-photo-5922101.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=800",
  },
  {
    n: "02",
    title: "Built for the Australian athlete",
    body: "Cold-packed, discreetly shipped express from within Australia. No customs delays, no month-long waits — dispatched fast, coast to coast.",
    img: "https://images.pexels.com/photos/12698200/pexels-photo-12698200.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=800",
  },
  {
    n: "03",
    title: "Recover. Lean out. Perform.",
    body: "From injury repair to summer-ready recomposition to raw output — a focused range chosen for the goals that matter, backed by the research literature.",
    img: "https://images.pexels.com/photos/29526364/pexels-photo-29526364.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=800",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [goals, setGoals] = useState([]);
  const [stacks, setStacks] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    fetchProducts().then((all) => {
      setFeatured(all.filter((p) => p.featured).sort((a, b) => (b.is_new ? 1 : 0) - (a.is_new ? 1 : 0)).slice(0, 3));
      setNewProducts(all.filter((p) => p.is_new && p.in_stock));
    });
    fetchGoals().then((d) => setGoals(d.goals));
    fetchStacks().then(setStacks).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-center overflow-hidden">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1754475118668-64ac3f3b2559?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
            alt=""
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-ink-900/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/30 to-ink-900/60" />
        </motion.div>

        <motion.div
          style={{ opacity: fade }}
          className="relative max-w-[1400px] mx-auto w-full px-5 md:px-10 pt-28"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-3 mb-7"
          >
            <span className="h-px w-10 bg-accent" />
            <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-zinc-300">
              Australia's Premier Research Peptides
            </span>
          </motion.div>

          <h1 className="font-display font-black tracking-tighter leading-[0.88] text-6xl sm:text-7xl lg:text-[9rem]">
            <MaskedLines lines={["HEAL FASTER.", "GET LEANER."]} delay={0.15} />
            <MaskedLines
              lines={["PERFORM HARDER."]}
              lineClassName="text-stroke-accent"
              delay={0.4}
            />
          </h1>

          <div className="mt-9 grid md:grid-cols-2 gap-8 items-end max-w-4xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="text-zinc-300 text-base md:text-lg leading-relaxed"
            >
              Clinical-grade research peptides for athletes, gym-goers and anyone chasing faster
              recovery and a summer-ready physique. Lab tested. Australian dispatched.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.8 }}
              className="flex flex-wrap gap-3"
            >
              <Link
                to="/shop"
                className="group flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-display font-bold uppercase tracking-wide text-base px-7 py-4 transition-colors clip-corner"
                data-testid="hero-shop-button"
              >
                Shop the Range
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/shop?goal=recovery"
                className="flex items-center gap-2 border border-white/20 hover:border-white text-white font-display font-bold uppercase tracking-wide text-base px-7 py-4 transition-colors"
                data-testid="hero-recovery-button"
              >
                Recovery Peptides
              </Link>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-zinc-500">Scroll</span>
          <ArrowDown size={16} className="text-accent animate-bounce" />
        </motion.div>
      </section>

      <Marquee />

      {/* NEW ARRIVALS */}
      {newProducts.length > 0 && (
        <section className="bg-ink-800/40 border-b border-[#27272A] py-20 md:py-24 overflow-hidden" data-testid="new-arrivals">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10">
            <Reveal className="flex items-end justify-between gap-6 mb-12">
              <div>
                <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">
                  / Just landed
                </span>
                <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-none">
                  NEW ARRIVALS
                </h2>
                <p className="text-zinc-400 mt-3 max-w-md leading-relaxed text-sm">
                  {newProducts.length} fresh research compounds just added to the range — launch pricing live now.
                </p>
              </div>
              <Link
                to="/shop"
                className="hidden sm:inline-flex font-mono-tech text-xs uppercase tracking-widest text-accent hover:text-white transition-colors whitespace-nowrap"
                data-testid="new-arrivals-view-all"
              >
                View all →
              </Link>
            </Reveal>
          </div>
          <div className="max-w-[1400px] mx-auto pl-5 md:pl-10">
            <div className="flex gap-5 overflow-x-auto pb-4 pr-5 md:pr-10 snap-x snap-mandatory [scrollbar-width:thin]" data-testid="new-arrivals-row">
              {newProducts.map((p) => (
                <div key={p.handle} className="min-w-[260px] sm:min-w-[300px] max-w-[300px] snap-start">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* GOALS */}
      <section id="goals" className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32">
        <Reveal className="max-w-3xl mb-14">
          <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">
            / Choose your goal
          </span>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">
            WHAT ARE YOU CHASING THIS SUMMER?
          </h2>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {goals.map((g, i) => (
            <Reveal key={g.slug} delay={i * 0.08}>
              <Link
                to={`/shop?goal=${g.slug}`}
                className="group relative block aspect-[3/4] overflow-hidden clip-corner border border-[#27272A] hover:border-accent/60 transition-colors"
                data-testid={`goal-card-${g.slug}`}
              >
                <img
                  src={GOAL_IMG[g.slug]}
                  alt={g.name}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent" />
                <span className="absolute top-4 left-4 font-mono-tech text-xs text-zinc-400">
                  0{i + 1}
                </span>
                <div className="absolute bottom-0 p-5">
                  <h3 className="font-display font-extrabold text-2xl leading-none tracking-tight group-hover:text-accent transition-colors">
                    {g.name}
                  </h3>
                  <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{g.tagline}</p>
                  <span className="inline-flex items-center gap-1 mt-4 font-mono-tech text-[10px] uppercase tracking-widest text-white">
                    Explore <ArrowRight size={12} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
      <section className="bg-ink-800/40 border-y border-[#27272A]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">
                / Most wanted
              </span>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">
                THE HEADLINE ACTS
              </h2>
            </div>
            <Link
              to="/shop"
              className="flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest text-zinc-300 hover:text-accent transition-colors"
            >
              View all products <ArrowRight size={14} />
            </Link>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((p, i) => (
              <Reveal key={p.handle} delay={i * 0.08}>
                <ProductCard product={p} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* BUNDLE DEALS */}
      {stacks.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">
                / Stack &amp; save
              </span>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">
                BUNDLE DEALS
              </h2>
              <p className="text-zinc-400 mt-4 max-w-xl leading-relaxed">
                Purpose-built protocols at a saving — add one in a tap. Build your own on the stack
                builder to unlock up to 30% off.
              </p>
            </div>
            <Link
              to="/build"
              className="flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest text-zinc-300 hover:text-accent transition-colors"
            >
              Build your own <ArrowRight size={14} />
            </Link>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {stacks.map((s, i) => (
              <StackCard key={s.slug} stack={s} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* MANIFESTO */}
      <section id="manifesto" className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32">
        <Reveal className="mb-16">
          <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">
            / The ethos
          </span>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-7xl mt-4 leading-[0.9] max-w-4xl">
            WE DON'T CUT CORNERS. WE ENGINEER RESULTS.
          </h2>
        </Reveal>

        <div className="space-y-20 md:space-y-0">
          {CHAPTERS.map((c, i) => (
            <div
              key={c.n}
              className={`grid md:grid-cols-12 gap-6 md:gap-10 items-center ${
                i > 0 ? "md:mt-24" : ""
              }`}
            >
              <Reveal
                className={`md:col-span-6 ${i % 2 === 1 ? "md:order-2" : ""}`}
                delay={0.05}
              >
                <div className="relative overflow-hidden clip-corner-lg border border-[#27272A]">
                  <img src={c.img} alt={c.title} className="w-full aspect-[4/3] object-cover" />
                  <span className="absolute top-4 left-4 font-display font-black text-7xl md:text-8xl text-white/15 leading-none">
                    {c.n}
                  </span>
                </div>
              </Reveal>
              <Reveal className={`md:col-span-6 ${i % 2 === 1 ? "md:order-1" : ""}`} delay={0.12}>
                <span className="font-display font-black text-8xl text-zinc-800 leading-none block mb-4">
                  {c.n}
                </span>
                <h3 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight leading-none mb-4">
                  {c.title}
                </h3>
                <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
                  {c.body}
                </p>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-t border-[#27272A] bg-ink-800/40">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 grid md:grid-cols-3 gap-8">
          {[
            { icon: FlaskConical, t: "Lab Tested > 99%", d: "Third-party HPLC verified purity on every batch." },
            { icon: Truck, t: "Express AU Shipping", d: "Cold-packed & dispatched fast, Australia-wide." },
            { icon: ShieldCheck, t: "Secure Checkout", d: "Encrypted payments. Discreet plain packaging." },
          ].map((f, i) => (
            <Reveal key={i} delay={i * 0.08} className="flex gap-4">
              <f.icon size={28} className="text-accent shrink-0" strokeWidth={1.5} />
              <div>
                <h4 className="font-display font-bold text-lg leading-none">{f.t}</h4>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{f.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
